import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface GalleryItem {
    id: string;
    title: string;
    img: string;
    drivelink: string;
    createdate: string;
}

export const ManageGallery = () => {
    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
    const [newItem, setNewItem] = useState({
        title: '',
        drivelink: '',
        imageFile: null as File | null,
        img: '',
        contentType: ''
    });

    useEffect(() => {
        fetchGalleryItems();
    }, []);

    useEffect(() => {
        if (editingItem) {
            setNewItem({
                title: editingItem.title,
                drivelink: editingItem.drivelink,
                imageFile: null,
                img: editingItem.img,
                contentType: ''
            });
        } else {
            setNewItem({
                title: '',
                drivelink: '',
                imageFile: null,
                img: '',
                contentType: ''
            });
        }
    }, [editingItem]);

    const fetchGalleryItems = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.get('/api/admin/managegallery?admin=true');
            if (response.data.result === 'Success') {
                // Ensure we always have an array, even if the API returns an empty object
                const data = response.data.data;
                const galleryArray = Array.isArray(data) ? data : Object.values(data || {});
                setGalleryItems(galleryArray);
            } else {
                toast.error(response.data.msg || 'Failed to fetch gallery items');
            }
        } catch (error) {
            toast.error('Failed to fetch gallery items');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddGalleryItem = async () => {
        if (!newItem.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!newItem.imageFile) {
            toast.error('Image is required');
            return;
        }
        setLoading(true);
        try {
            let data: any = {
                title: newItem.title,
                drivelink: newItem.drivelink,
                img: newItem.img
            };
            if (editingItem) {
                data.galleryId = editingItem.id;
            }
            if (newItem.imageFile) {
                const imageBase64 = await fileToBase64(newItem.imageFile);
                data.imageBase64 = imageBase64;
                data.imageFileName = newItem.imageFile.name;
                data.contentType = newItem.contentType;
            }
            let response;
            if (editingItem) {
                response = await AxiosAdminInstance.put('/api/admin/managegallery', data);
            } else {
                response = await AxiosAdminInstance.post('/api/admin/managegallery', data);
            }
            if (response.data.result === 'Success') {
                toast.success(editingItem ? 'Gallery item updated successfully' : 'Gallery item added successfully');
                setNewItem({
                    title: '',
                    drivelink: '',
                    imageFile: null,
                    img: '',
                    contentType: ''
                });
                setEditingItem(null);
                fetchGalleryItems();
            } else {
                toast.error(response.data.msg || 'Operation failed');
            }
        } catch (error) {
            toast.error(editingItem ? 'Failed to update gallery item' : 'Failed to add gallery item');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewItem({
            title: '',
            drivelink: '',
            imageFile: null,
            img: '',
            contentType: ''
        });
        setEditingItem(null);
    };

    const handleDeleteGalleryItem = async (galleryId: string) => {
        if (!window.confirm('Are you sure you want to delete this gallery item?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.delete('/api/admin/managegallery', {
                params: { galleryId }
            });
            if (response.data.result === 'Success') {
                toast.success('Gallery item deleted successfully');
                fetchGalleryItems();
            } else {
                toast.error(response.data.msg || 'Failed to delete gallery item');
            }
        } catch (error) {
            toast.error('Failed to delete gallery item');
        }
        setLoading(false);
    };

    const handleDeleteClick = (e: React.MouseEvent, galleryId: string) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Delete clicked for gallery ID:', galleryId);
        handleDeleteGalleryItem(galleryId);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (2MB max)
            if (file.size > 2 * 1024 * 1024) {
                toast.error('File size must be less than 2MB');
                return false;
            }
            setNewItem(prev => ({ ...prev, imageFile: file, img: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const galleryColumns = [
        { 
            name: 'Actions', 
            cell: (row: GalleryItem) => (
                <div>
                    <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingItem(row)} title="Edit">
                        <i className="fa fa-edit"></i>
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={(e) => handleDeleteClick(e, row.id)} title="Delete">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ) 
        },
        { 
            name: 'Title', 
            selector: (row: GalleryItem) => row.title, 
            sortable: true,
            width: '250px'
        },
        { 
            name: 'Date', 
            selector: (row: GalleryItem) => new Date(row.createdate).toLocaleDateString(), 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'Drive Link', 
            cell: (row: GalleryItem) => (
                row.drivelink ? (
                    <a href={row.drivelink} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info">
                        View Gallery
                    </a>
                ) : 'No Link'
            ), 
            sortable: false,
            width: '150px'
        },
        { 
            name: 'Image', 
            cell: (row: GalleryItem) => (
                row.img ? (
                    <img src={row.img} alt={row.title} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                ) : 'No Image'
            ), 
            sortable: false,
            width: '100px'
        }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-camera fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Gallery</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddGalleryItem(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="title"
                                            custplaceholder="Gallery Title"
                                            custid="galleryTitle"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, title: val }))}
                                            onExternalBind={newItem.title}
                                        />
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="drivelink"
                                            custplaceholder="Drive Link (Optional)"
                                            custid="galleryDriveLink"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, drivelink: val }))}
                                            onExternalBind={newItem.drivelink}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="image"
                                            custplaceholder="Upload Image"
                                            custid="galleryImage"
                                            onChange={handleFileChange}
                                        />
                                        {newItem.img && (
                                            <div className="mt-2">
                                                <img src={newItem.img} alt="Current" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn-default" type="submit" disabled={loading}>
                                            {editingItem ? 'Update Gallery Item' : 'Add Gallery Item'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingItem && (
                                            <button className="btn-default" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Gallery Items</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={galleryColumns}
                                            data={galleryItems}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};